/** Formata bytes em B / KB / MB legível. */
export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Tamanho máximo de anexo de negócio (bucket deal-files). */
const DEAL_FILE_MAX_BYTES = 50 * 1024 * 1024; // 50 MB

/** MIME types aceitos pelo bucket `deal-files` (espelha a migração). */
const DEAL_FILE_ACCEPTED_MIME = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "text/csv",
  "text/plain",
  "application/zip",
  "application/x-zip-compressed",
  "video/mp4",
] as const;

/** Extensões aceitas (fallback p/ navegadores que não preenchem o MIME, ex.: .csv/.heic). */
const DEAL_FILE_ACCEPTED_EXT = [
  "jpg", "jpeg", "png", "gif", "webp", "svg",
  "pdf", "xlsx", "xls", "docx", "doc", "pptx", "ppt",
  "csv", "txt", "zip", "mp4",
];

/**
 * Valida um arquivo contra os limites do bucket `deal-files`.
 * Retorna `null` se válido, ou uma mensagem de erro pronta para toast.
 */
export function validateDealFile(file: File): string | null {
  if (file.size > DEAL_FILE_MAX_BYTES) {
    return `"${file.name}" excede o limite de 50 MB.`;
  }
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mimeOk = !!file.type && (DEAL_FILE_ACCEPTED_MIME as readonly string[]).includes(file.type);
  const extOk = DEAL_FILE_ACCEPTED_EXT.includes(ext);
  if (!mimeOk && !extOk) {
    return `Tipo de arquivo não suportado: "${file.name}". Aceitos: PDF, imagens, vídeo MP4, Word, Excel, PowerPoint, CSV, TXT, ZIP.`;
  }
  return null;
}

/** `accept` para <input type="file"> e react-dropzone. */
export const DEAL_FILE_INPUT_ACCEPT = "." + DEAL_FILE_ACCEPTED_EXT.join(",.");

/**
 * Sanitiza o nome do arquivo para uso como chave (key) no Supabase Storage.
 * O Storage rejeita keys com caracteres fora do conjunto ASCII permitido
 * (ex.: acentos como "ç"/"é" → erro "Invalid key"). Aqui removemos acentos e
 * trocamos qualquer caractere não-alfanumérico por "_". O nome original deve
 * continuar sendo guardado à parte (ex.: coluna file_name) para exibição.
 */
export function sanitizeStorageFileName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^\w.\-]+/g, "_")
    .replace(/_{2,}/g, "_");
}

/** Emoji de ícone por MIME type (fallback genérico de anexo). */
export function getFileIcon(fileType: string): string {
  if (!fileType) return "📎";
  if (fileType.startsWith("image/")) return "🖼️";
  if (fileType.startsWith("video/")) return "🎬";
  if (fileType === "application/pdf") return "📄";
  if (fileType.includes("spreadsheet") || fileType.includes("excel") || fileType === "text/csv") return "📊";
  if (fileType.includes("presentation") || fileType.includes("powerpoint")) return "📽️";
  if (fileType.includes("document") || fileType.includes("word")) return "📝";
  if (fileType.includes("zip")) return "🗜️";
  return "📎";
}
